//register.js

import { useState, useEffect } from "react";
import { FaEye, FaEyeSlash, FaUpload, FaCheck, FaTimes, FaInfoCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ─────────────────────────────────────────────────────
   TOAST NOTIFICATION COMPONENT — Temple Themed
───────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  const { t } = useTranslation();
  
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: {
      icon: <FaCheck />,
      label: t("toastBlessings"),
      accentColor: "#2a6b2a",
      accentLight: "rgba(42,107,42,0.15)",
      iconBg: "rgba(42,107,42,0.12)",
      borderColor: "rgba(42,107,42,0.3)",
      topBarColor: "#2a6b2a",
      symbol: "✦"
    },
    error: {
      icon: <FaTimes />,
      label: t("toastError"),
      accentColor: "#8b1a1a",
      accentLight: "rgba(139,26,26,0.12)",
      iconBg: "rgba(139,26,26,0.1)",
      borderColor: "rgba(139,26,26,0.28)",
      topBarColor: "#8b1a1a",
      symbol: "✗"
    },
    info: {
      icon: <FaInfoCircle />,
      label: t("toastDivineNotice"),
      accentColor: "#8a6020",
      accentLight: "rgba(201,151,58,0.1)",
      iconBg: "rgba(201,151,58,0.1)",
      borderColor: "rgba(201,151,58,0.3)",
      topBarColor: "#c9973a",
      symbol: "ॐ"
    }
  };

  const c = config[type] || config.info;

  return (
    <div className="toast-container">
      <div className="toast-card" style={{ "--accent": c.accentColor, "--accent-light": c.accentLight, "--border-color": c.borderColor, "--top-bar": c.topBarColor }}>
        <div className="toast-top-bar" />
        <span className="toast-corner tl" />
        <span className="toast-corner tr" />
        <span className="toast-corner bl" />
        <span className="toast-corner br" />
        <div className="toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="toast-medallion-ring" />
          <div className="toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>
        <div className="toast-content">
          <div className="toast-label-row">
            <span className="toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="toast-rule" style={{ background: `linear-gradient(90deg, transparent, ${c.accentColor}55, transparent)` }} />
          <p className="toast-message">{message}</p>
        </div>
        <button className="toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>
        <div className="toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   ILLUSTRATOR PANEL
───────────────────────────────────────────────────── */
function IllustratorPanel({ illusSymbol }) {
  const { t } = useTranslation();
  return (
    <div className="illus-panel">
      <div className="illus-rings">
        {[280, 230, 185, 145, 110].map((s, i) => (
          <div key={s} className="illus-ring"
            style={{ width: s, height: s, animationDelay: `${i * 0.4}s` }} />
        ))}
      </div>

      <div className="illus-medallion">
        <div className="medallion-outer-ring" />
        <div className="medallion-inner">
          <div className={`medallion-face ${illusSymbol === "om" ? "visible" : ""}`}>
            <span className="illus-om">ॐ</span>
          </div>
          <div className={`medallion-face ${illusSymbol === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple Logo" className="illus-logo-img" />
          </div>
        </div>
        <div className="medallion-halo" />
      </div>

      <div className="illus-info">
        <div className="illus-rule" />
        {/* Using shared translations */}
        <div className="illus-name">{t("ftTempleName")}</div>
        <div className="illus-sub">{t("ftTempleSub")}</div>
        <div className="illus-loc">✦ {t("templeLoc")} ✦</div>
        <div className="illus-rule" />
      </div>

      <div className="illus-quote">
        <p>{t("hpQuote")}</p>
      </div>

      <div className="illus-dots">
        {[...Array(5)].map((_, i) => (
          <span key={i} className="illus-dot" style={{ opacity: i === 2 ? 0.7 : 0.25 }} />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   PAGE SHELL
───────────────────────────────────────────────────── */
function PageShell({ children, illusSymbol }) {
  return (
    <div className="login-page">
      <div className="outer-card">
        <div className="outer-card-strip" />
        <div className="login-split">
          <div className="login-left">
            <IllustratorPanel illusSymbol={illusSymbol} />
          </div>
          <div className="login-right">{children}</div>
        </div>
        <div className="outer-card-strip bottom" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   FORM CARD
───────────────────────────────────────────────────── */
function FormCard({ title, subtitle, children }) {
  return (
    <div className="form-card">
      <div className="form-header">
        <div className="fh-icon">
          <div className="fh-ring" />
          <span className="fh-om">ॐ</span>
        </div>
        <h2 className="form-title">{title}</h2>
        {subtitle && <p className="form-subtitle">{subtitle}</p>}
        <div className="form-title-rule" />
      </div>
      <div className="form-body">{children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   MAIN REGISTER COMPONENT
───────────────────────────────────────────────────── */
export default function Register() {
  const navigate = useNavigate();
  // 5️⃣ Use Translation Hook
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    mobile: '', password: '', name: '', email: '', address: '', place: ''
  });
  const [errors, setErrors] = useState({});
  const [availability, setAvailability] = useState({ mobile: null, email: null });
  const [showPass, setShowPass] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [illusSymbol, setIllusSymbol] = useState("om");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setIllusSymbol(s => s === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const newErrors = { ...errors };
    
    // Name validation
    if (formData.name && !formData.name.trim()) { newErrors.name = t("errName"); } else { delete newErrors.name; }
    
    // Mobile validation
    if (formData.mobile.length > 0) {
      if (!/^\d{10}$/.test(formData.mobile)) { newErrors.mobile = t("errMobile"); }
      else if (availability.mobile === false) { newErrors.mobile = t("errMobileTaken"); }
      else if (availability.mobile === true) { delete newErrors.mobile; }
    } else { delete newErrors.mobile; }
    
    // Password validation
    if (formData.password) {
      if (formData.password.length < 6) { newErrors.password = t("errPassLength"); }
      else { delete newErrors.password; }
    }
    
    // Email validation
    if (formData.email && formData.email.includes('@')) {
      if (availability.email === false) { newErrors.email = t("errEmailTaken"); }
      else if (availability.email === true) { delete newErrors.email; }
    } else { delete newErrors.email; }
    
    // Place validation
    if (formData.place && !formData.place.trim()) { newErrors.place = t("errPlace"); } else { delete newErrors.place; }
    
    // Address validation
    if (formData.address && !formData.address.trim()) { newErrors.address = t("errAddress"); } else { delete newErrors.address; }
    
    setErrors(newErrors);
  }, [formData, availability, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'mobile' && value.length === 10) checkAvailability('mobile', value);
    if (name === 'email' && value.includes('@')) checkAvailability('email', value);
  };

  const checkAvailability = async (field, value) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/check/${field}/${value}`);
      const data = await res.json();
      setAvailability(prev => ({ ...prev, [field]: data.available }));
    } catch (err) {
      console.error("Check failed", err);
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      if (selected.size > 2 * 1024 * 1024) {
        setToast({ message: t("errFileSize"), type: "error" });
        return;
      }
      setFile(selected);
      setErrors(prev => ({ ...prev, idProof: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t("errName");
    if (!/^\d{10}$/.test(formData.mobile)) newErrors.mobile = t("errMobile");
    if (formData.password.length < 6) newErrors.password = t("errPassLength");
    if (!formData.place.trim()) newErrors.place = t("errPlace");
    if (!formData.address.trim()) newErrors.address = t("errAddress");
    if (!file) newErrors.idProof = t("errIdProof");
    if (availability.mobile === false) newErrors.mobile = t("errMobileTaken");
    if (availability.email === false) newErrors.email = t("errEmailTaken");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const dataPayload = new FormData();
    Object.keys(formData).forEach(key => dataPayload.append(key, formData[key]));
    dataPayload.append('idProof', file);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/register`, { method: 'POST', body: dataPayload });
      const result = await res.json();
      if (res.ok) {
        setToast({ message: t("regSuccess"), type: "success" });
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setToast({ message: result.error || t("regFail"), type: "error" });
      }
    } catch (err) {
      setToast({ message: t("errServer"), type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageStyles />
      <Navbar />
      <PageShell illusSymbol={illusSymbol}>
        <FormCard title={t("regTitle")} subtitle={t("regSubtitle")}>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">

              {/* Name */}
              <div className="input-group full-width">
                <label className="input-label">{t("lblFullName")} <span className="req-mark">*</span></label>
                <div className={`input-wrap ${errors.name ? 'error' : ''}`}>
                  <input type="text" name="name" className="form-input" placeholder={t("phFullName")} value={formData.name} onChange={handleChange} />
                </div>
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              {/* Mobile */}
              <div className="input-group">
                <label className="input-label">{t("lblMobile")} <span className="req-mark">*</span></label>
                <div className={`input-wrap ${errors.mobile ? 'error' : ''}`}>
                  <span className="input-prefix">🇮🇳 +91</span>
                  <div className="input-sep" />
                  <input type="tel" name="mobile" className="form-input" placeholder={t("phMobileReg")} maxLength="10" value={formData.mobile} onChange={handleChange} />
                </div>
                {errors.mobile && <span className="field-error">{errors.mobile}</span>}
              </div>

              {/* Password */}
              <div className="input-group">
                <label className="input-label">{t("lblPassword")} <span className="req-mark">*</span></label>
                <div className={`input-wrap ${errors.password ? 'error' : ''}`}>
                  <input type={showPass ? "text" : "password"} name="password" className="form-input" placeholder={t("phPasswordReg")} value={formData.password} onChange={handleChange} />
                  <button type="button" className="eye-btn" onClick={() => setShowPass(!showPass)}>
                    {showPass ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.password && <span className="field-error">{errors.password}</span>}
              </div>

              {/* Email */}
              <div className="input-group">
                <label className="input-label">{t("lblEmail")}</label>
                <div className={`input-wrap ${errors.email ? 'error' : ''}`}>
                  <input type="email" name="email" className="form-input" placeholder={t("phEmail")} value={formData.email} onChange={handleChange} />
                </div>
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>

              {/* Place */}
              <div className="input-group">
                <label className="input-label">{t("lblPlace")} <span className="req-mark">*</span></label>
                <div className={`input-wrap ${errors.place ? 'error' : ''}`}>
                  <input type="text" name="place" className="form-input" placeholder={t("phPlace")} value={formData.place} onChange={handleChange} />
                </div>
                {errors.place && <span className="field-error">{errors.place}</span>}
              </div>

              {/* Address */}
              <div className="input-group full-width">
                <label className="input-label">{t("lblAddress")} <span className="req-mark">*</span></label>
                <div className={`input-wrap ${errors.address ? 'error' : ''}`}>
                  <input type="text" name="address" className="form-input" placeholder={t("phAddress")} value={formData.address} onChange={handleChange} />
                </div>
                {errors.address && <span className="field-error">{errors.address}</span>}
              </div>

              {/* ID Proof Upload */}
              <div className="input-group full-width">
                <label className="input-label">{t("lblIdProof")} <span className="req-mark">*</span></label>
                <div className={`upload-area ${errors.idProof ? 'error' : ''}`} onClick={() => document.getElementById('id-upload').click()}>
                  <input id="id-upload" type="file" className="file-input" accept="image/*,.pdf" onChange={handleFileChange} />
                  <div className="upload-icon"><FaUpload /></div>
                  <div className="upload-text">{file ? file.name : t("uploadText")}</div>
                  <div className="upload-sub">{t("uploadSub")}</div>
                </div>
                {errors.idProof && <span className="field-error">{errors.idProof}</span>}
                {file && (
                  <div className="file-preview">
                    <FaCheck /> {file.name} {t("fileSelected")}
                  </div>
                )}
              </div>
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? t("btnRegistering") : t("btnRegister")}
            </button>
          </form>

          <div className="form-divider">
            <span className="divider-line" />
            <span className="divider-text">{t("or")}</span>
            <span className="divider-line" />
          </div>

          <p className="form-footer-text">
            {t("alreadyDevotee")}{" "}
            <button className="text-link" onClick={() => navigate('/login')}>{t("loginAccount")}</button>
          </p>
        </FormCard>
      </PageShell>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Footer />
    </>
  );
}

/* ═══════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════ */
function PageStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --crimson-deep:  #1a0404;
        --crimson-mid:   #2c0808;
        --crimson-rich:  #3d0c0c;
        --gold-bright:   #e8c46a;
        --gold-mid:      #c9973a;
        --gold-dim:      #8a6020;
        --gold-pale:     #f5e4b0;
        --parchment:     #FDF3E3;
        --text-dark:     #1a0404;
        --text-mid:      #4a1a08;
      }

      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

      /* ═══ PAGE ═══ */
      .login-page {
        background: radial-gradient(ellipse at 30% 0%, rgba(201,151,58,0.07) 0%, transparent 60%), var(--parchment);
        min-height: calc(100vh - 160px);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 48px 24px;
      }

      /* ═══ OUTER CARD ═══ */
      .outer-card {
        width: 100%;
        max-width: 1100px;
        border: 1px solid rgba(201,151,58,0.3);
        box-shadow: 0 0 0 4px rgba(201,151,58,0.06), 0 0 0 8px rgba(201,151,58,0.03), 0 32px 80px rgba(26,4,4,0.22);
        clip-path: polygon(0 20px, 20px 0, calc(100% - 20px) 0, 100% 20px, 100% calc(100% - 20px), calc(100% - 20px) 100%, 20px 100%, 0 calc(100% - 20px));
        overflow: hidden;
        animation: outerCardIn 0.6s cubic-bezier(0.23,1,0.32,1) both;
      }
      @keyframes outerCardIn {
        from { opacity: 0; transform: scale(0.97) translateY(20px); }
        to   { opacity: 1; transform: scale(1) translateY(0); }
      }

      .outer-card-strip {
        height: 3px;
        background: linear-gradient(90deg, transparent, var(--gold-dim) 10%, var(--gold-bright) 30%, #fff8e7 50%, var(--gold-bright) 70%, var(--gold-dim) 90%, transparent);
      }
      .outer-card-strip.bottom {
        background: linear-gradient(90deg, transparent, var(--gold-dim) 15%, var(--gold-mid) 50%, var(--gold-dim) 85%, transparent);
      }

      /* ═══ SPLIT GRID ═══ */
      .login-split {
        display: grid;
        grid-template-columns: 1fr 1.2fr;
        min-height: 650px;
      }

      /* ═══ LEFT PANEL ═══ */
      .login-left {
        background: radial-gradient(ellipse at 50% 25%, rgba(120,25,25,0.55) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(60,10,10,0.4) 0%, transparent 50%), linear-gradient(160deg, #2c0a0a 0%, #1a0404 55%, #0e0101 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 52px 36px;
        position: relative;
        overflow: hidden;
        border-right: 1px solid rgba(201,151,58,0.15);
      }
      .login-left::before {
        content: '';
        position: absolute;
        inset: 0;
        background-image: url("data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E");
        opacity: 0.5;
        pointer-events: none;
      }

      /* ═══ RIGHT PANEL ═══ */
      .login-right {
        background: var(--parchment);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 52px 44px;
        position: relative;
        overflow: hidden;
      }

      /* ═══ ILLUSTRATOR ═══ */
      .illus-panel { position: relative; display: flex; flex-direction: column; align-items: center; z-index: 1; width: 100%; max-width: 340px; }
      .illus-rings { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); width: 290px; height: 290px; pointer-events: none; }
      .illus-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.08); animation: gentlePulse 4s ease-in-out infinite alternate; }
      .illus-ring:nth-child(odd) { border-style: dashed; }
      @keyframes gentlePulse { from { opacity: 0.4; } to { opacity: 0.9; } }

      .illus-medallion { width: 110px; height: 110px; position: relative; display: flex; align-items: center; justify-content: center; margin-bottom: 28px; margin-top: 40px; flex-shrink: 0; }
      .medallion-outer-ring { position: absolute; inset: 0; border-radius: 50%; background: conic-gradient(from 0deg, transparent 0deg, var(--gold-bright) 40deg, var(--gold-mid) 80deg, transparent 120deg, var(--gold-bright) 160deg, transparent 200deg, var(--gold-mid) 240deg, transparent 280deg, var(--gold-bright) 320deg, transparent 360deg) border-box; border: 2px solid transparent; -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0); -webkit-mask-composite: destination-out; mask-composite: exclude; animation: slowSpin 10s linear infinite; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .medallion-inner { position: absolute; inset: 10px; border-radius: 50%; background: radial-gradient(circle, #3d1010 0%, #1a0404 70%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.25); box-shadow: inset 0 0 20px rgba(0,0,0,0.7), 0 0 20px rgba(232,196,106,0.08); overflow: hidden; display: flex; align-items: center; justify-content: center; }
      .medallion-face { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.6s ease, transform 0.6s ease; border-radius: 50%; }
      .medallion-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .illus-om { font-family: 'Cinzel Decorative', cursive; font-size: 36px; background: linear-gradient(180deg, #fff8e7 0%, var(--gold-bright) 50%, var(--gold-mid) 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 2px 8px rgba(232,196,106,0.6)); }
      .illus-logo-img { width: 62px; height: 62px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .medallion-halo { position: absolute; inset: -8px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.12) 0%, transparent 70%); pointer-events: none; }
      .illus-info { display: flex; flex-direction: column; align-items: center; gap: 5px; margin-bottom: 20px; width: 100%; }
      .illus-rule { width: 70%; height: 1px; background: linear-gradient(90deg, transparent, rgba(232,196,106,0.35), transparent); margin: 6px 0; }
      .illus-name { font-family: 'Cinzel Decorative', cursive; font-size: 15px; font-weight: 900; letter-spacing: 1px; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; text-align: center; line-height: 1.4; }
      .illus-sub { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(232,196,106,0.5); text-align: center; }
      .illus-loc { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; text-transform: uppercase; color: rgba(232,196,106,0.3); text-align: center; }
      .illus-quote { margin-bottom: 20px; padding: 0 8px; }
      .illus-quote p { font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px; color: rgba(245,228,176,0.4); text-align: center; line-height: 1.75; }
      .illus-dots { display: flex; gap: 8px; align-items: center; }
      .illus-dot { width: 5px; height: 5px; background: var(--gold-bright); transform: rotate(45deg); display: block; }

      /* ═══ FORM CARD ═══ */
      .form-card { width: 100%; max-width: 650px; position: relative; z-index: 1; }
      .form-header { text-align: center; padding: 20px 32px 10px; }
      .fh-icon { width: 54px; height: 54px; position: relative; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; }
      .fh-ring { position: absolute; inset: 0; border-radius: 50%; background: conic-gradient(from 0deg, transparent 0deg, var(--gold-bright) 60deg, transparent 120deg, var(--gold-mid) 180deg, transparent 240deg, var(--gold-bright) 300deg, transparent 360deg) border-box; border: 1.5px solid transparent; -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0); -webkit-mask-composite: destination-out; mask-composite: exclude; animation: slowSpin 8s linear infinite; }
      .fh-om { font-family: 'Cinzel Decorative', cursive; font-size: 18px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; position: relative; z-index: 1; }
      .form-title { font-family: 'Cinzel Decorative', cursive; font-size: 18px; font-weight: 900; letter-spacing: 0.5px; background: linear-gradient(135deg, #3d0c0c, var(--crimson-deep)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 6px; }
      .form-subtitle { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.55); line-height: 1.5; }
      .form-title-rule { width: 60%; height: 1px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent); margin: 16px auto 0; }

      /* ── Form body ── */
      .form-body { padding: 24px 32px 36px; display: flex; flex-direction: column; gap: 16px; }
      .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
      .full-width { grid-column: span 2; }
      .input-group { display: flex; flex-direction: column; gap: 6px; position: relative; }
      .label-row { display: flex; justify-content: space-between; align-items: center; }
      .input-label { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .req-mark { color: #b52020; font-weight: 900; margin-left: 2px; }
      .input-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); transition: border-color 0.3s, box-shadow 0.3s, background 0.3s; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; position: relative; }
      .input-wrap:focus-within { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .input-wrap.error { border-color: #b52020; background: rgba(181,32,32,0.02); }
      .input-prefix { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; color: var(--text-mid); padding: 13px 12px 13px 20px; border-right: 1px solid rgba(201,151,58,0.2); flex-shrink: 0; }
      .input-sep { width: 1px; height: 20px; background: rgba(201,151,58,0.2); flex-shrink: 0; }
      .form-input { flex: 1; background: transparent; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; }
      .form-input::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .eye-btn { background: none; border: none; cursor: pointer; padding: 0 14px; color: rgba(74,26,8,0.35); transition: color 0.2s; }
      .eye-btn:hover { color: var(--gold-mid); }
      .field-error { font-size: 10px; color: #b52020; margin-top: 4px; font-family: 'EB Garamond', serif; font-style: italic; }

      /* Upload */
      .upload-area { border: 1px dashed rgba(201,151,58,0.4); background: rgba(255,249,240,0.5); padding: 20px; text-align: center; cursor: pointer; transition: all 0.3s; position: relative; clip-path: polygon(10px 0%, 100% 0%, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0% 100%, 0% 10px); }
      .upload-area:hover { border-color: var(--gold-mid); background: rgba(255,250,240,0.8); }
      .upload-icon { font-size: 24px; color: var(--gold-dim); margin-bottom: 8px; }
      .upload-text { font-size: 13px; color: var(--text-mid); font-family: 'EB Garamond', serif; }
      .upload-sub { font-size: 11px; color: rgba(74,26,8,0.5); margin-top: 4px; }
      .file-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
      .file-preview { margin-top: 8px; font-size: 11px; color: #2a6b2a; display: flex; align-items: center; justify-content: center; gap: 6px; font-family: 'Cinzel', serif; }

      /* Buttons */
      .submit-btn { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 15px 28px; cursor: pointer; width: 100%; display: flex; align-items: center; justify-content: center; gap: 10px; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3), inset 0 1px 0 rgba(255,255,255,0.25); transition: all 0.35s ease; position: relative; overflow: hidden; margin-top: 30px; }
      .submit-btn::before { content: ''; position: absolute; top: -50%; left: -70%; width: 40%; height: 200%; background: rgba(255,255,255,0.25); transform: skewX(-18deg); transition: left 0.55s ease; pointer-events: none; }
      .submit-btn:hover::before { left: 130%; }
      .submit-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); filter: brightness(1.05); }
      .submit-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; box-shadow: none; }
      .text-link { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--gold-dim); background: none; border: none; cursor: pointer; padding: 0; transition: color 0.2s; text-decoration: underline; text-underline-offset: 3px; text-decoration-color: rgba(138,96,32,0.3); }
      .text-link:hover { color: var(--gold-mid); }
      .form-divider { display: flex; align-items: center; gap: 12px; margin: 10px 0; }
      .divider-line { flex: 1; height: 1px; background: rgba(201,151,58,0.2); }
      .divider-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px; color: rgba(74,26,8,0.3); }
      .form-footer-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.42); text-align: center; }

      /* ══════════════════════════════════════════
         TOAST — Temple Sacred Parchment Theme
      ══════════════════════════════════════════ */
      .toast-container {
        position: fixed;
        top: 28px;
        right: 28px;
        z-index: 2000;
        animation: toastSlideIn 0.45s cubic-bezier(0.23, 1, 0.32, 1) both;
      }
      @keyframes toastSlideIn {
        from { opacity: 0; transform: translateX(60px) scale(0.95); }
        to   { opacity: 1; transform: translateX(0)   scale(1);    }
      }

      .toast-card {
        position: relative;
        width: 340px;
        background: linear-gradient(145deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%);
        border: 1px solid var(--border-color);
        padding: 0 0 14px 0;
        overflow: hidden;
        clip-path: polygon(
          0 12px, 12px 0,
          calc(100% - 12px) 0, 100% 12px,
          100% calc(100% - 12px), calc(100% - 12px) 100%,
          12px 100%, 0 calc(100% - 12px)
        );
        box-shadow:
          0 4px 6px rgba(26,4,4,0.06),
          0 12px 32px rgba(26,4,4,0.14),
          0 0 0 1px rgba(201,151,58,0.08),
          inset 0 1px 0 rgba(255,255,255,0.7);
      }

      .toast-top-bar {
        height: 3px;
        background: linear-gradient(
          90deg,
          transparent 0%,
          var(--accent) 20%,
          var(--accent) 80%,
          transparent 100%
        );
        margin-bottom: 16px;
      }

      .toast-corner {
        position: absolute;
        width: 10px;
        height: 10px;
        border-color: rgba(201,151,58,0.45);
        border-style: solid;
      }
      .toast-corner.tl { top: 12px;  left: 12px;  border-width: 1px 0 0 1px; }
      .toast-corner.tr { top: 12px;  right: 12px; border-width: 1px 1px 0 0; }
      .toast-corner.bl { bottom: 8px; left: 12px;  border-width: 0 0 1px 1px; }
      .toast-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }

      .toast-medallion {
        position: absolute;
        left: 18px;
        top: 26px;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 1px solid;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
      .toast-medallion-ring {
        position: absolute;
        inset: -3px;
        border-radius: 50%;
        border: 1px dashed rgba(201,151,58,0.3);
        animation: toastSpin 8s linear infinite;
      }
      @keyframes toastSpin { to { transform: rotate(360deg); } }
      .toast-icon-inner { font-size: 14px; position: relative; z-index: 1; }

      .toast-content { padding: 0 40px 0 66px; }

      .toast-label-row {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 5px;
      }
      .toast-symbol { font-family: 'Cinzel Decorative', cursive; font-size: 9px; opacity: 0.7; }
      .toast-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .toast-rule { height: 1px; margin-bottom: 7px; }
      .toast-message { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.75); line-height: 1.5; }

      .toast-bottom-rule {
        height: 1px;
        margin: 12px 20px 0;
        background: linear-gradient(90deg, transparent, rgba(201,151,58,0.25), transparent);
      }

      .toast-close {
        position: absolute;
        top: 22px;
        right: 14px;
        background: none;
        border: none;
        font-size: 13px;
        cursor: pointer;
        opacity: 0.45;
        line-height: 1;
        padding: 2px 4px;
        transition: opacity 0.2s;
        font-family: sans-serif;
      }
      .toast-close:hover { opacity: 0.9; }

      /* ═══ RESPONSIVE ═══ */
      @media (max-width: 860px) {
        .login-page { padding: 0; align-items: stretch; min-height: calc(100vh - 160px); background: var(--parchment); }
        .outer-card { clip-path: none; border: none; box-shadow: none; width: 100%; }
        .login-split { grid-template-columns: 1fr; min-height: auto; }
        .login-left { display: none; }
        .login-right { padding: 40px 24px; background: transparent; }
        .form-card { clip-path: none; border-radius: 2px; max-width: 100%; }
        .outer-card-strip { display: none; }
        .form-grid { grid-template-columns: 1fr; gap: 16px; }
        .full-width { grid-column: span 1; }
        .toast-container { top: 16px; right: 16px; }
        .toast-card { width: 300px; }
      }
      @media (max-width: 480px) {
        .login-right { padding: 28px 16px; }
        .form-body { padding: 20px 18px 28px; gap: 14px; }
        .form-header { padding: 24px 18px 0; }
        .form-title { font-size: 15px; }
        .toast-card { width: 280px; }
      }
    `}</style>
  );
}